import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { isPDFWorkerRegistered } from '../../lib_enter';

// 文件类型枚举
export type FileType = 'pdf' | 'image' | 'text' | 'markdown' | 'markdown_table' | 'html' | 'unknown';

// 解析结果接口
export interface ParseResult {
  type: FileType;
  content: any;
  fileName?: string;
  fileType?: string;
  totalPages?: number;
  error?: string;
}

// 解析选项接口
export interface ParseOptions {
  fileName?: string;
  fileType?: string;
  token?: string;
}

/**
 * 确保PDF Worker已注册
 * 注意：此函数不会自动注册 Worker，只检查是否已注册
 * Worker 应该由最终调用方通过 registerPDFWorker 注册
 */
const ensurePDFWorkerRegistered = () => {
  // 检查是否已经注册过worker
  if (isPDFWorkerRegistered()) {
    console.log('PDF Worker 已经注册');
    return;
  }

  // Worker 未注册，抛出友好的错误提示
  const errorMsg =
    'PDF Worker 未注册。请在应用入口处调用 registerPDFWorker() 注册 Worker。\n' +
    '示例：import { registerPDFWorker } from "@mxmweb/fviewer";\n' +
    '      registerPDFWorker("/worker/pdf.worker.min.js");\n' +
    '注意：Worker 文件路径需要根据实际部署情况调整。';

  console.error(errorMsg);
  throw new Error(errorMsg);
};

/**
 * 检测文件类型
 * @param fileName - 文件名
 * @param fileType - 文件类型
 * @param content - 文件内容
 * @returns 文件类型
 */
/** 调用方显式传入的已知类型，优先于根据 fileName 的推断 */
const EXPLICIT_FILE_TYPES: FileType[] = ['pdf', 'image', 'text', 'markdown', 'markdown_table', 'html'];

export const detectFileType = (fileName?: string, fileType?: string, content?: any): FileType => {
  if (!fileName && !fileType && !content) return 'unknown';

  const name = fileName || '';
  const type = (fileType || '').toLowerCase();

  // 调用方显式指定了文件类型时优先使用，避免「选文本但文件名是 .pdf」被误判为 PDF
  if (type === 'pdf_slides') return 'pdf';
  if (EXPLICIT_FILE_TYPES.includes(type as FileType)) return type as FileType;

  if (name.toLowerCase().endsWith('.pdf') || content?.type === 'pdf') {
    return 'pdf';
  }
  if (name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/) || content?.type === 'image') {
    return 'image';
  }
  if (name.toLowerCase().endsWith('.md') || content?.type === 'markdown') {
    if (content?.type === 'markdown_table') return 'markdown_table';
    return 'markdown';
  }
  if (name.toLowerCase().match(/\.(html|htm)$/) || content?.type === 'html') {
    return 'html';
  }
  if (name.toLowerCase().match(/\.(txt|log|json|xml|css|js|ts)$/) || content?.type === 'text') {
    return 'text';
  }

  return 'unknown';
};

/**
 * 解析PDF文件（支持静态文件和stream chunk）
 * @param data - PDF数据（ArrayBuffer或URL）
 * @param options - 解析选项
 * @returns 解析结果
 */
export const parsePdfFile = async (data: ArrayBuffer | string, options: ParseOptions = {}): Promise<ParseResult> => {
  console.log('=== parsePdfFile 开始 ===');
  console.log('输入数据:', { data: typeof data, options });

  try {
    // 确保PDF Worker已注册
    ensurePDFWorkerRegistered();

    let pdfDocument: pdfjsLib.PDFDocumentProxy;

    if (typeof data === 'string') {
      console.log('从URL加载PDF:', data);
      // 静态文件URL
      const loadingTask = pdfjsLib.getDocument({
        url: data,
        httpHeaders: options.token ? { 'Authorization': options.token } : undefined,
      });
      console.log('=== PDF加载任务详细信息 ===');
      console.log('加载任务对象:', loadingTask);
      console.log('加载任务类型:', typeof loadingTask);
      console.log('加载任务方法:', Object.getOwnPropertyNames(Object.getPrototypeOf(loadingTask)));
      console.log('加载任务属性:', Object.keys(loadingTask));

      pdfDocument = await loadingTask.promise;
      console.log('=== PDF文档加载完成 ===');
      console.log('PDF文档对象:', pdfDocument);
      console.log('PDF文档类型:', typeof pdfDocument);
      console.log('PDF文档方法:', Object.getOwnPropertyNames(Object.getPrototypeOf(pdfDocument)));
      console.log('PDF文档属性:', Object.keys(pdfDocument));
    } else {
      console.log('从ArrayBuffer加载PDF');
      // ArrayBuffer数据（stream chunk）
      const loadingTask = pdfjsLib.getDocument({
        data: data,
      });
      console.log('=== PDF加载任务详细信息 ===');
      console.log('加载任务对象:', loadingTask);
      console.log('加载任务类型:', typeof loadingTask);

      pdfDocument = await loadingTask.promise;
      console.log('=== PDF文档加载完成 ===');
      console.log('PDF文档对象:', pdfDocument);
      console.log('PDF文档类型:', typeof pdfDocument);
    }

    const totalPages = pdfDocument.numPages;
    console.log('PDF加载成功，总页数:', totalPages);
    console.log('PDF指纹:', pdfDocument.fingerprints);
    console.log('PDF传输对象:', pdfDocument._transport);

    const result: ParseResult = {
      type: 'pdf' as FileType,
      content: {
        type: 'pdf',
        document: pdfDocument,
        totalPages,
      },
      fileName: options.fileName,
      fileType: 'pdf',
      totalPages,
    };

    console.log('=== 解析结果详细信息 ===');
    console.log('解析结果:', result);
    console.log('解析结果类型:', typeof result);
    console.log('解析结果属性:', Object.keys(result));
    console.log('内容对象:', result.content);
    console.log('文档对象:', result.content.document);

    return result;
      } catch (error) {
      console.error('PDF解析失败:', error);
      console.error('错误详情:', {
        message: error instanceof Error ? error.message : '未知错误',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
        url: typeof data === 'string' ? data : 'ArrayBuffer',
        options: options
      });

      // 提供更详细的错误信息
      let errorMessage = 'PDF解析失败';
      if (error instanceof Error) {
        if (error.message.includes('PDF Worker 未注册') || error.message.includes('Worker 未注册')) {
          // Worker 未注册的错误，保留原始错误信息，让用户知道需要注册
          errorMessage = error.message;
        } else if (error.message.includes('Invalid PDF structure')) {
          errorMessage = 'PDF文件结构无效，可能文件损坏或不是有效的PDF文件';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'PDF文件下载失败，请检查网络连接和文件URL';
        } else if (error.message.includes('Worker') || error.message.includes('worker')) {
          errorMessage = 'PDF解析器初始化失败。请确保在应用入口处调用 registerPDFWorker() 注册 Worker 文件。';
        } else {
          errorMessage = `PDF解析失败: ${error.message}`;
        }
      }

      return {
        type: 'pdf',
        content: null,
        fileName: options.fileName,
        fileType: 'pdf',
        error: errorMessage,
      };
    }
};

/**
 * 解析图片文件
 * @param data - 图片数据（URL或ArrayBuffer）
 * @param options - 解析选项
 * @returns 解析结果
 */
export const parseImageFile = async (data: ArrayBuffer | string, options: ParseOptions = {}): Promise<ParseResult> => {
  console.log('=== parseImageFile 开始 ===');
  console.log('输入数据:', { data: typeof data, options });

  try {
    let imageUrl: string;

    if (typeof data === 'string') {
      console.log('从URL加载图片:', data);
      // 静态文件URL
      imageUrl = data;
    } else {
      console.log('从ArrayBuffer加载图片');
      // ArrayBuffer数据，转换为blob URL
      const blob = new Blob([data]);
      imageUrl = URL.createObjectURL(blob);
    }

    // 预加载图片以验证是否能正常加载
    console.log('开始预加载图片:', imageUrl);
    await new Promise<void>((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        console.log('图片预加载成功:', imageUrl);
        console.log('图片尺寸:', img.width, 'x', img.height);
        resolve();
      };

      img.onerror = () => {
        console.error('图片预加载失败:', imageUrl);
        reject(new Error('图片加载失败'));
      };

      // 设置超时时间（10秒）
      const timeout = setTimeout(() => {
        reject(new Error('图片加载超时'));
      }, 10000);

      img.onload = () => {
        clearTimeout(timeout);
        console.log('图片预加载成功:', imageUrl);
        console.log('图片尺寸:', img.width, 'x', img.height);
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timeout);
        console.error('图片预加载失败:', imageUrl);
        reject(new Error('图片加载失败'));
      };

      img.src = imageUrl;
    });

    const result: ParseResult = {
      type: 'image',
      content: {
        type: 'image',
        url: imageUrl,
      },
      fileName: options.fileName,
      fileType: 'image',
    };

    console.log('=== parseImageFile 结果 ===');
    console.log('result:', result);

    return result;
  } catch (error) {
    console.error('图片解析失败:', error);
    return {
      type: 'image',
      content: null,
      fileName: options.fileName,
      fileType: 'image',
      error: `图片解析失败: ${error instanceof Error ? error.message : '未知错误'}`,
    };
  }
};

/**
 * 解析文本文件
 * @param data - 文本数据（URL或ArrayBuffer）
 * @param options - 解析选项
 * @returns 解析结果
 */
export const parseTextFile = async (data: ArrayBuffer | string, options: ParseOptions = {}): Promise<ParseResult> => {
  try {
    let textContent: string;

    if (typeof data === 'string') {
      // 静态文件URL
      const response = await fetch(data, {
        headers: options.token ? { 'Authorization': options.token } : undefined,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      textContent = await response.text();
    } else {
      // ArrayBuffer数据
      const decoder = new TextDecoder('utf-8');
      textContent = decoder.decode(data);
    }

    return {
      type: 'text',
      content: {
        type: 'text',
        text: textContent,
      },
      fileName: options.fileName,
      fileType: 'text',
    };
  } catch (error) {
    return {
      type: 'text',
      content: null,
      fileName: options.fileName,
      fileType: 'text',
      error: `文本解析失败: ${error instanceof Error ? error.message : '未知错误'}`,
    };
  }
};

/**
 * 解析Markdown文件
 * @param data - Markdown数据（URL或ArrayBuffer）
 * @param options - 解析选项
 * @returns 解析结果
 */
export const parseMarkdownFile = async (data: ArrayBuffer | string, options: ParseOptions = {}): Promise<ParseResult> => {
  try {
    let markdownContent: string;

    if (typeof data === 'string') {
      // 静态文件URL
      const response = await fetch(data, {
        headers: options.token ? { 'Authorization': options.token } : undefined,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      markdownContent = await response.text();
    } else {
      // ArrayBuffer数据
      const decoder = new TextDecoder('utf-8');
      markdownContent = decoder.decode(data);
    }

    return {
      type: 'markdown',
      content: {
        type: 'markdown',
        text: markdownContent,
      },
      fileName: options.fileName,
      fileType: 'markdown',
    };
  } catch (error) {
    return {
      type: 'markdown',
      content: null,
      fileName: options.fileName,
      fileType: 'markdown',
      error: `Markdown解析失败: ${error instanceof Error ? error.message : '未知错误'}`,
    };
  }
};

/**
 * 解析HTML文件
 * @param data - HTML数据（URL或ArrayBuffer）
 * @param options - 解析选项
 * @returns 解析结果
 */
export const parseHtmlFile = async (data: ArrayBuffer | string, options: ParseOptions = {}): Promise<ParseResult> => {
  console.log('=== parseHtmlFile 开始 ===');
  console.log('输入数据:', { data: typeof data, options });

  try {
    let htmlContent: string;

    if (typeof data === 'string') {
      console.log('从URL加载HTML:', data);
      // 静态文件URL
      const response = await fetch(data, {
        headers: options.token ? { 'Authorization': options.token } : undefined,
      });
      console.log('HTTP响应状态:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      htmlContent = await response.text();
      console.log('HTML内容长度:', htmlContent.length);
      console.log('HTML内容前500字符:', htmlContent.substring(0, 500));
    } else {
      console.log('从ArrayBuffer加载HTML');
      // ArrayBuffer数据
      const decoder = new TextDecoder('utf-8');
      htmlContent = decoder.decode(data);
    }

    const result: ParseResult = {
      type: 'html' as FileType,
      content: {
        type: 'html',
        text: htmlContent,
      },
      fileName: options.fileName,
      fileType: 'html',
    };

    console.log('=== parseHtmlFile 结果 ===');
    console.log('result:', result);
    console.log('result.content.text 长度:', result.content.text.length);
    console.log('result.content.text 前200字符:', result.content.text.substring(0, 200));

    return result;
  } catch (error) {
    console.error('HTML解析失败:', error);
    return {
      type: 'html',
      content: null,
      fileName: options.fileName,
      fileType: 'html',
      error: `HTML解析失败: ${error instanceof Error ? error.message : '未知错误'}`,
    };
  }
};

/**
 * 统一文件解析函数
 * @param data - 文件数据（URL或ArrayBuffer）
 * @param options - 解析选项
 * @returns 解析结果
 */
export const parseFile = async (data: ArrayBuffer | string, options: ParseOptions = {}): Promise<ParseResult> => {
  const fileType = detectFileType(options.fileName, options.fileType);

  switch (fileType) {
    case 'pdf':
      return await parsePdfFile(data, options);
    case 'image':
      return await parseImageFile(data, options);
    case 'text':
      return await parseTextFile(data, options);
    case 'markdown':
    case 'markdown_table':
      return await parseMarkdownFile(data, options);
    case 'html':
      return await parseHtmlFile(data, options);
    default:
      return {
        type: 'unknown',
        content: null,
        fileName: options.fileName,
        fileType: options.fileType,
        error: '不支持的文件类型',
      };
  }
};
